import {
    autoUpdate,
    flip,
    offset,
    shift,
    useFloating,
} from "@floating-ui/react";
import { faChevronDown } from "@fortawesome/pro-light-svg-icons";
import { faIndustry } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popover } from "@headlessui/react";
import { atom, useAtom } from "jotai";
import { atomFamily, atomWithStorage } from "jotai/utils";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import { formatDateTime, getTimezoneInfo } from "../../../utils/dates";
import { type EventFormFieldProps, FormFieldBase, SuggestionBox } from "./base";

export const formDatetimeTimezoneBySite = atomWithStorage<
    Record<string, "site" | "utc" | string>
>("formDatetimeFieldTimezoneBySite", {}, undefined, { getOnInit: true });

export const formDatetimeTimezone = atomFamily((siteId: string) =>
    atom(
        (get) => get(formDatetimeTimezoneBySite)[siteId] ?? "site",
        (get, set, value: string) =>
            set(formDatetimeTimezoneBySite, {
                ...get(formDatetimeTimezoneBySite),
                [siteId]: value,
            }),
    ),
);

const US_TIMEZONES: { zone: string; label: string }[] = [
    { zone: "America/New_York", label: "Eastern" },
    { zone: "America/Chicago", label: "Central" },
    { zone: "America/Denver", label: "Mountain" },
    { zone: "America/Phoenix", label: "Arizona" },
    { zone: "America/Los_Angeles", label: "Pacific" },
    { zone: "America/Anchorage", label: "Alaska" },
    { zone: "Pacific/Honolulu", label: "Hawaii" },
];

const OTHER_OFFSETS = [
    "-12:00",
    "-11:00",
    "-10:00",
    "-09:30",
    "-09:00",
    "-08:00",
    "-07:00",
    "-06:00",
    "-05:00",
    "-04:00",
    "-03:30",
    "-03:00",
    "-02:00",
    "-01:00",
    "+01:00",
    "+02:00",
    "+03:00",
    "+03:30",
    "+04:00",
    "+04:30",
    "+05:00",
    "+05:30",
    "+05:45",
    "+06:00",
    "+06:30",
    "+07:00",
    "+08:00",
    "+09:00",
    "+09:30",
    "+10:00",
    "+10:30",
    "+11:00",
    "+12:00",
    "+13:00",
    "+14:00",
];

type TzRow = {
    key: string;
    offset: string;
    label: string;
    value: string;
    isSite?: boolean;
};

const offsetToMinutes = (off: string) => {
    const sign = off[0] === "-" ? -1 : 1;
    const [h, m] = off.slice(1).split(":").map(Number);
    return sign * (h * 60 + m);
};

const TimezoneRowList = ({
    timezone,
    siteInfo,
    detectionDate,
    onSelect,
}: {
    timezone: string;
    siteInfo: { offset?: string; abbrev?: string };
    detectionDate: Date;
    onSelect: (value: string) => void;
}) => {
    const rows = useMemo<TzRow[]>(() => {
        const usDerived = US_TIMEZONES.map(({ zone, label }) => {
            const dt = DateTime.fromJSDate(detectionDate).setZone(zone);
            if (!dt.isValid) return null;
            return {
                zone,
                name: label,
                offset: dt.toFormat("ZZ"),
                abbrev: dt.toFormat("ZZZZ"),
            };
        }).filter((r) => r !== null);

        // Denver and Arizona coincide (-07:00 MST) in winter; merge zones
        // sharing an offset and abbreviation into one row.
        const usGroups = new Map<string, (typeof usDerived)[number][]>();
        for (const r of usDerived) {
            const k = `${r.offset}|${r.abbrev}`;
            usGroups.set(k, [...(usGroups.get(k) ?? []), r]);
        }
        const usUnique: TzRow[] = [...usGroups.values()].map((group) => ({
            key: `us-${group[0].zone}`,
            offset: group[0].offset,
            label: `${group.map((r) => r.name).join(", ")} (${group[0].abbrev})`,
            value: group[0].offset,
        }));

        const usOffsetSet = new Set(usUnique.map((r) => r.offset));
        const bareRows: TzRow[] = OTHER_OFFSETS.filter(
            (off) => !usOffsetSet.has(off),
        ).map((off) => ({
            key: `off-${off}`,
            offset: off,
            label: "",
            value: off,
        }));

        const siteRow: TzRow | null =
            siteInfo.offset != null
                ? {
                      key: "site",
                      offset: siteInfo.offset,
                      label: siteInfo.abbrev ? `(${siteInfo.abbrev})` : "",
                      value: "site",
                      isSite: true,
                  }
                : null;

        const utcRow: TzRow = {
            key: "utc",
            offset: "+00:00",
            label: "",
            value: "utc",
        };

        // The site row already covers its offset; drop the other rows that
        // would show the same offset a second time.
        const all = [
            ...(siteRow ? [siteRow] : []),
            utcRow,
            ...usUnique,
            ...bareRows,
        ].filter((r) => r.isSite || r.offset !== siteInfo.offset);
        all.sort(
            (a, b) => offsetToMinutes(a.offset) - offsetToMinutes(b.offset),
        );
        return all;
    }, [detectionDate, siteInfo.offset, siteInfo.abbrev]);

    const rowClass = (selected: boolean) =>
        `w-full text-left px-3 py-1.5 text-sm cursor-pointer transition-colors ${
            selected
                ? "bg-ae-blue-100 text-ae-blue-900 font-medium"
                : "text-neutral-700 hover:bg-ae-gray-50"
        }`;

    return (
        <div className="py-1 overflow-y-auto max-h-60">
            {rows.map((row) => (
                <button
                    key={row.key}
                    type="button"
                    onClick={() => onSelect(row.value)}
                    className={rowClass(timezone === row.value)}
                >
                    <span className="flex items-center gap-1.5">
                        {row.isSite && (
                            <FontAwesomeIcon
                                icon={faIndustry}
                                className="w-3 text-ae-blue-550"
                            />
                        )}
                        <span>
                            UTC{row.offset}
                            {row.label && ` ${row.label}`}
                        </span>
                        {row.isSite && (
                            <span className="ml-1 text-[10px] font-bold uppercase tracking-wide text-ae-blue-550 bg-ae-blue-100 rounded px-1 py-0.5">
                                Site time zone
                            </span>
                        )}
                    </span>
                </button>
            ))}
        </div>
    );
};

const TimezonePicker = ({
    timezone,
    setTimezone,
    siteTimezone,
    detectionDate,
}: {
    timezone: string;
    setTimezone: (value: string) => void;
    siteTimezone?: string | null;
    detectionDate: Date;
}) => {
    const { refs, floatingStyles } = useFloating({
        strategy: "fixed",
        placement: "bottom-end",
        middleware: [offset({ mainAxis: 4 }), shift(), flip()],
        whileElementsMounted: autoUpdate,
    });

    const siteInfo = useMemo(() => {
        if (!siteTimezone) return { offset: undefined, abbrev: undefined };
        const dt = DateTime.fromJSDate(detectionDate).setZone(siteTimezone);
        if (!dt.isValid) return { offset: undefined, abbrev: undefined };
        return { offset: dt.toFormat("ZZ"), abbrev: dt.toFormat("ZZZZ") };
    }, [siteTimezone, detectionDate]);

    const triggerLabel =
        timezone === "utc"
            ? "UTC"
            : timezone === "site"
              ? siteInfo.offset
                  ? `UTC${siteInfo.offset}`
                  : "Site"
              : `UTC${timezone}`;

    return (
        <div ref={refs.setReference}>
            <Popover>
                {({ close }) => (
                    <>
                        <Popover.Button className="flex items-center gap-1 px-2 py-1.5 text-sm rounded border border-neutral-200 bg-white hover:bg-ae-gray-50 text-ae-blue-550 min-w-[3.5rem] justify-center">
                            {timezone === "site" && (
                                <FontAwesomeIcon
                                    icon={faIndustry}
                                    className="w-3 text-ae-blue-550"
                                />
                            )}
                            <span>{triggerLabel}</span>
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                className="w-3"
                            />
                        </Popover.Button>
                        <Popover.Panel
                            ref={refs.setFloating}
                            style={floatingStyles}
                            className="w-80 flex flex-col rounded-md border-2 border-ae-slate-300 drop-shadow z-50 bg-white"
                        >
                            <TimezoneRowList
                                timezone={timezone}
                                siteInfo={siteInfo}
                                detectionDate={detectionDate}
                                onSelect={(value) => {
                                    setTimezone(value);
                                    close();
                                }}
                            />
                            <div className="border-t border-neutral-200 px-3 py-2 text-xs text-neutral-400">
                                <p>
                                    Defaults to site time zone at date of
                                    detection.
                                </p>
                            </div>
                        </Popover.Panel>
                    </>
                )}
            </Popover>
        </div>
    );
};

export const FormDateTimeInput = (props: EventFormFieldProps) => {
    const form = useFormContext();
    const fieldName = props.fieldDefinition.fieldName;
    const site =
        props.emissionRecord.site ?? props.emissionRecord.siteMatches[0];
    const [fieldTimezone, setFieldTimezone] = useAtom(
        formDatetimeTimezone(site?.id ?? "no-site"),
    );
    const siteTimezone = site?.timeZone;
    const detectionDate = props.emissionRecord.dataPoint.detectionTimestamp;
    const { offset: siteOffset } = useMemo(
        () => getTimezoneInfo(siteTimezone, detectionDate),
        [siteTimezone, detectionDate],
    );
    // The site offset is frozen at the detection timestamp, so entered values
    // always align with what the site's clocks read when the emission was
    // detected. A bare offset ("-06:00") is valid in an ISO string for
    // parsing; luxon wants the "UTC"-prefixed form as a zone for display.
    const useUtc =
        fieldTimezone === "utc" ||
        (fieldTimezone === "site" && siteOffset == null);
    const timeOffsetString = useUtc
        ? "Z"
        : fieldTimezone === "site"
          ? siteOffset
          : fieldTimezone;
    const displayZone = useUtc ? "utc" : `UTC${timeOffsetString}`;

    const { field, fieldState } = useController({
        name: fieldName,
        control: form.control,
    });

    const toLocalValue = useCallback(
        (isoString: string) => {
            const localizedDate =
                DateTime.fromISO(isoString).setZone(displayZone);
            if (!localizedDate.isValid) {
                return { dateString: "", timeString: "" };
            }
            return {
                dateString: localizedDate.toFormat("yyyy-MM-dd"),
                timeString: localizedDate.toFormat("HH:mm"),
            };
        },
        [displayZone],
    );

    // Local states for time and date
    const [dateValue, setDateValue] = useState(
        toLocalValue(field.value).dateString,
    );
    const [timeValue, setTimeValue] = useState(
        toLocalValue(field.value).timeString,
    );

    // Sync effects
    // 1. When timezone is toggled
    // 2. When value is externally updated
    useEffect(() => {
        setDateValue(toLocalValue(field.value).dateString);
        setTimeValue(toLocalValue(field.value).timeString);
    }, [toLocalValue, field.value]);

    const buildIso = (date: string, time: string): string | null => {
        const parsed = new Date(`${date}T${time}:00${timeOffsetString}`);
        return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
    };

    const commitToFormValue = (dateStr: string) => {
        field.onChange(dateStr);
    };

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = e.target.value;
        setDateValue(newDate);
        if (!newDate) {
            commitToFormValue("");
            return;
        }
        const iso = buildIso(newDate, timeValue || "00:00");
        if (iso != null) commitToFormValue(iso);
    };

    const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!dateValue) return;
        const newTime = e.target.value;
        setTimeValue(newTime);
        const iso = buildIso(dateValue, newTime || "00:00");
        if (iso != null) commitToFormValue(iso);
    };

    const inputClass = `rounded px-2 py-1.5 text-sm border ${
        fieldState.error
            ? "bg-red-50 border-red-500 text-red-500"
            : "border-neutral-200"
    }`;

    return (
        <FormFieldBase
            label={props.fieldDefinition.label}
            error={fieldState.error}
        >
            <div className="flex gap-2 items-start">
                <div className="flex-1 flex flex-col">
                    <input
                        type="date"
                        className={inputClass}
                        value={dateValue}
                        onChange={handleDateChange}
                        onBlur={field.onBlur}
                        ref={field.ref}
                    />
                    {dateValue && (
                        <button
                            type="button"
                            className="text-xs text-gray-400 hover:text-gray-600 transition-colors mt-1 self-start"
                            onClick={() => {
                                setTimeValue("");
                                setDateValue("");
                                field.onChange("");
                                field.onBlur();
                            }}
                        >
                            &times; Clear date and time
                        </button>
                    )}
                </div>
                <div className="w-32 flex flex-col">
                    <input
                        type="time"
                        className={`${inputClass} ${!dateValue ? "opacity-50" : ""}`}
                        value={timeValue}
                        onChange={handleTimeChange}
                        onBlur={field.onBlur}
                        disabled={!dateValue}
                    />
                </div>
                <TimezonePicker
                    timezone={fieldTimezone}
                    setTimezone={setFieldTimezone}
                    siteTimezone={siteTimezone}
                    detectionDate={detectionDate}
                />
            </div>
            {(field.value == null || field.value === "") &&
                props.suggestedValue != null && (
                    <SuggestionBox
                        suggestedValueLabel={formatDateTime(
                            new Date(props.suggestedValue as string),
                        )}
                        onApply={() => field.onChange(props.suggestedValue)}
                    />
                )}
        </FormFieldBase>
    );
};

export const FormDateInput = (props: EventFormFieldProps) => {
    const form = useFormContext();
    const fieldName = props.fieldDefinition.fieldName;

    const { field, fieldState } = useController({
        name: fieldName,
        control: form.control,
    });

    const toInputValue = (value: string): string => {
        if (!value) return "";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return "";
        return date.toISOString().slice(0, 10);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        field.onChange(val ? new Date(val + "T00:00:00Z").toISOString() : "");
    };

    return (
        <FormFieldBase
            label={props.fieldDefinition.label}
            error={fieldState.error}
        >
            <input
                type="date"
                className={`rounded px-2 py-1.5 text-sm w-full border ${
                    fieldState.error
                        ? "bg-red-50 border-red-500 text-red-500"
                        : "border-neutral-200"
                }`}
                value={toInputValue(field.value)}
                onChange={handleChange}
                onBlur={field.onBlur}
                ref={field.ref}
            />
            {(field.value == null || field.value === "") &&
                props.suggestedValue != null && (
                    <SuggestionBox
                        suggestedValueLabel={new Date(
                            props.suggestedValue as string,
                        ).toLocaleDateString()}
                        onApply={() => field.onChange(props.suggestedValue)}
                    />
                )}
        </FormFieldBase>
    );
};
